import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, PackageSearch, SplitSquareHorizontal, Timer, X } from "lucide-react";
import { api, fmtMoney, type JarayondaSale, type Product } from "../api";
import { fmtDateTime } from "../lib/format";
import { useBranch } from "../lib/use-branch";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Money, Mono } from "../components/ui/ledger";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { JarayondaPartialDialog } from "../components/jarayonda-partial-dialog";
import { ProductCombobox } from "../components/product-combobox";
import { QtyStepper } from "../components/qty-stepper";

/**
 * Jarayonda — band qilingan, hali yakunlanmagan sotuvlar (Ombor 5-tabi).
 *
 * Usta tovarni mijozga olib chiqadi: zaxira kamaymaydi, lekin o'sha tovarni
 * boshqa hech kim sota olmaydi. Ustaxona egasi bu yerdan bandlikni to'liq
 * tasdiqlaydi (sotildi → oddiy `active` sotuvga aylanadi, to'liq to'langan
 * deb yoziladi) yoki bekor qiladi (tovar zaxiraga qaytadi, iz qolmaydi).
 *
 * Qisman qaytarish ("Qisman qaytarish" tugmasi) — bir qismi sotildi, bir qismi
 * qaytdi: egasi har qator uchun tasdiqlanadigan miqdorni kiritadi, qolgani
 * bandlikdan bo'shaydi. Hammasi qaytarilsa nol summali sotuv yozilmaydi —
 * bandlik butunlay bekor qilinadi.
 * Mijoz maydoni yo'q: bandlikda mijoz tanlanmaydi (u faqat tasdiqlashda
 * aniqlanadi), shuning uchun ustunda "—" turadi.
 */
export function JarayondaPage({ openSignal }: { openSignal?: number }) {
  const { t } = useTranslation(["ombor", "common"]);
  const [rows, setRows] = useState<JarayondaSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [creating, setCreating] = useState(false);
  // Qisman qaytarish dialogi ochilgan hujjat (shartli mount — mount == ochilish).
  const [partialRow, setPartialRow] = useState<JarayondaSale | null>(null);
  // Amal bajarilayotgan hujjat — barcha tugmalar bloklanadi (ikki marta bosish).
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setRows(await api.jarayondaSales());
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("common:common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, t]);

  // Tablar qatoridagi "Yangi bandlik" tugmasi (OmborPage) signal orqali ochadi.
  useEffect(() => {
    if (openSignal) setCreating(true);
  }, [openSignal]);

  async function confirm(row: JarayondaSale) {
    if (!window.confirm(t("jarayonda.confirmPrompt"))) return;
    setBusyId(row.id);
    try {
      // To'liq tasdiqlash: band qilingan miqdorning hammasi sotilgan.
      // `returned_items` yuborilmaydi — backend sotilgan + qaytgan = band
      // qilingan shartini shu holatda ham qanoatlantiradi.
      await api.confirmJarayonda(row.id, {
        sold_items: row.items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
      });
      toast.success(t("jarayonda.confirmed"));
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusyId(null);
    }
  }

  async function cancel(row: JarayondaSale) {
    if (!window.confirm(t("jarayonda.cancelPrompt"))) return;
    setBusyId(row.id);
    try {
      await api.cancelJarayonda(row.id);
      toast.success(t("jarayonda.cancelled"));
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Timer />}
              title={t("jarayonda.empty.title")}
              description={t("jarayonda.empty.desc")}
              action={
                <Button onClick={() => setCreating(true)}>
                  {t("jarayonda.new")}
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("jarayonda.table.sana")}</TableHead>
                  <TableHead>{t("jarayonda.table.usta")}</TableHead>
                  <TableHead>{t("jarayonda.table.mijoz")}</TableHead>
                  <TableHead>{t("jarayonda.table.tovarlar")}</TableHead>
                  <TableHead className="text-right">
                    {t("jarayonda.table.summa")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("jarayonda.table.amallar")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">
                      <Mono className="text-muted-foreground">
                        {fmtDateTime(r.created_at)}
                      </Mono>
                    </TableCell>
                    <TableCell>{r.created_by_name || "—"}</TableCell>
                    {/* Bandlikda mijoz tanlanmaydi — u tasdiqlashda aniqlanadi. */}
                    <TableCell className="text-muted-foreground">—</TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {r.items.map((i) => (
                          <div key={i.product_id} className="text-sm">
                            {i.product_nomi}
                            <span className="text-muted-foreground">
                              {" "}
                              × {Number(i.quantity)}
                            </span>
                          </div>
                        ))}
                        {r.izoh && (
                          <div className="text-xs text-muted-foreground">
                            {r.izoh}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Money>{fmtMoney(r.jami_summa ?? 0)}</Money>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          className="h-7"
                          disabled={busyId != null}
                          onClick={() => confirm(r)}
                        >
                          <Check />
                          {t("jarayonda.actions.confirm")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={busyId != null}
                          onClick={() => setPartialRow(r)}
                        >
                          <SplitSquareHorizontal />
                          {t("jarayonda.actions.partial")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={busyId != null}
                          onClick={() => cancel(r)}
                        >
                          <X />
                          {t("jarayonda.actions.cancel")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={creating}
        onOpenChange={(o) => !o && setCreating(false)}
        contentClassName="max-w-2xl p-0"
      >
        {creating && (
          <JarayondaForm
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      {partialRow && (
        <JarayondaPartialDialog
          open={!!partialRow}
          onOpenChange={(o) => !o && setPartialRow(null)}
          sale={partialRow}
          onDone={() => setRefresh((x) => x + 1)}
        />
      )}
    </div>
  );
}

type Line = { product: Product; miqdor: string };

/**
 * Yangi bandlik formasi — sotuv formasining soddalashtirilgani: narx ham,
 * mijoz ham yo'q (ikkalasi tasdiqlashda aniqlanadi). Ko'rsatilgan summa
 * tovar kartochkasi narxidagi taxminiy qiymat.
 */
function JarayondaForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId } = useBranch();
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [izoh, setIzoh] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setProducts(
          await api.products({
            faol: true,
            branch_id: selectedBranchId ?? undefined,
          }),
        );
      } catch {
        /* empty */
      }
    })();
  }, [selectedBranchId]);

  const usedIds = useMemo(() => new Set(lines.map((l) => l.product.id)), [lines]);
  const total = useMemo(
    () =>
      lines.reduce(
        (s, l) => s + (Number(l.miqdor) || 0) * (Number(l.product.sotuv_narx) || 0),
        0,
      ),
    [lines],
  );

  const setLine = useCallback((i: number, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }, []);

  function addProduct(p: Product) {
    setLines((ls) =>
      ls.some((l) => l.product.id === p.id)
        ? ls
        : [...ls, { product: p, miqdor: String(Math.min(1, Number(p.joriy_ostatka))) }],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      setErr(t("jarayonda.form.atLeastOne"));
      return;
    }
    // Ombor qoidasi (FE) — backend baribir band qilinganini ham hisobga oladi.
    const over = lines.find(
      (l) => (Number(l.miqdor) || 0) > Number(l.product.joriy_ostatka),
    );
    if (over) {
      setErr(t("jarayonda.form.overStock", { nomi: over.product.nomi }));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.createJarayonda({
        items: lines.map((l) => ({
          product_id: l.product.id,
          quantity: l.miqdor || "0",
        })),
        izoh: izoh.trim() || null,
        branch_id: selectedBranchId ?? undefined,
        // Idempotentlik kaliti — takroriy yuborish dublikat yaratmaydi.
        client_uuid: crypto.randomUUID(),
      });
      toast.success(t("jarayonda.form.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex max-h-[85vh] w-full flex-col">
      <div className="flex-none space-y-3 border-b p-4 sm:p-5">
        <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
          {t("jarayonda.form.title")}
        </h2>
        <ProductCombobox
          products={products}
          excludeIds={usedIds}
          onPick={addProduct}
          unavailable={(p) => Number(p.joriy_ostatka) <= 0}
          disabled={busy}
        />
        <Input
          value={izoh}
          onChange={(e) => setIzoh(e.target.value)}
          disabled={busy}
          placeholder={t("jarayonda.form.izohPlaceholder")}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-[120px]" />
            <col className="w-[140px]" />
            <col className="w-[40px]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
            <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left">{t("sotuvlar.form.tovar")}</th>
              <th className="px-2 py-2 text-center">{t("sotuvlar.form.miqdor")}</th>
              <th className="px-3 py-2 text-right">{t("sotuvlar.form.jami")}</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-10 text-center text-sm text-muted-foreground"
                >
                  <PackageSearch className="mx-auto mb-2 size-6 opacity-60" />
                  {t("jarayonda.form.emptyHint")}
                </td>
              </tr>
            ) : (
              lines.map((l, i) => {
                const avail = Number(l.product.joriy_ostatka);
                return (
                  <tr key={l.product.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="truncate font-medium" title={l.product.nomi}>
                        {l.product.nomi}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {avail} {l.product.birlik}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <QtyStepper
                        value={l.miqdor}
                        onChange={(v) => setLine(i, { miqdor: v })}
                        disabled={busy}
                        max={avail}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {fmtMoney(
                        (Number(l.miqdor) || 0) * (Number(l.product.sotuv_narx) || 0),
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setLines((ls) => ls.filter((_, idx) => idx !== i))
                        }
                        disabled={busy}
                        className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                        aria-label={t("shared.delete")}
                      >
                        <X className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {err && (
          <div className="m-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}
      </div>

      <div className="flex-none rounded-b-lg border-t bg-muted/30 p-4 sm:p-5">
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("jarayonda.form.taxminiy")}
          </div>
          <div className="text-2xl font-bold tabular-nums">{fmtMoney(total)}</div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-none"
          >
            {t("sotuvlar.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={busy || lines.length === 0}
            className="flex-1"
          >
            {busy ? t("jarayonda.form.submitting") : t("jarayonda.form.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
